import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import ExcelTableV5 from '../ExcelTableV5';
import AnimatedNumberDisplay from '../ui/AnimatedNumberDisplay';
import { Dream, DreamConfig, DreamProjection } from '../../types/dreamSimulator';
import { formatCurrency, formatPercent } from '../../utils/formatters';
import { Investment } from '../../types';
import { compoundMultiplierContent } from '../../data/compoundMultiplierContent';
import { ChevronRight, ChevronLeft, BarChart3, LineChart, Activity, TrendingUp, DollarSign, Eye, EyeOff, Play, Pause, RotateCcw, SkipForward, Zap, Target, Award, Calendar, Settings, Download, FileText, CheckCircle, Plus, Minus, Edit } from 'lucide-react';
import { 
  ChevronRight, 
  ChevronLeft,
  Play,
  Pause,
  RotateCcw,
  SkipForward,
  BarChart3,
  Download,
  FileText,
  CheckCircle,
  Activity,
  TrendingUp,
  DollarSign
} from 'lucide-react';
import { ResponsiveContainer, LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, BarChart, Bar, AreaChart, Area } from 'recharts';

// Function to generate compound interest report
const generateCompoundInterestReport = (
  dream: Dream, 
  config: DreamConfig, 
  projections: DreamProjection[], 
  investment: Investment,
  onStatusChange?: (status: 'idle' | 'downloading' | 'complete') => void
) => {
  const currentDate = new Date();
  const formattedDate = currentDate.toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  // Custom investment data
  const customInvestment = {
    name: 'Mi Inversión',
    amount: config.certificatesNeeded * investment.certificateBasePrice,
    annualReturn: 8
  };

  const finalProjection = projections[projections.length - 1];
  const totalInvestment = config.certificatesNeeded * investment.certificateBasePrice;
  const finalMultiplier = finalProjection ? (finalProjection.patrimony / totalInvestment) : 0;
  
  // Find when goal is achieved
  const goalAchievementYear = projections.findIndex(p => 
    config.monthlyGoal ? (p.monthlyIncome / config.monthlyGoal) * 100 >= 100 : false
  ) + 1;
  
  const isGoalAchieved = goalAchievementYear > 0;
  const finalProgress = finalProjection && config.monthlyGoal ? 
    (finalProjection.monthlyIncome / config.monthlyGoal) * 100 : 0;

  const htmlContent = `
    <!DOCTYPE html>
    <html lang="es">
    <head>
      <meta charset="UTF-8">
      <title>Reporte de Interés Compuesto Multiplicador™ - ${dream.name}</title>
      <style>
        :root {
          --primary: #16a34a;
          --secondary: #6366f1;
          --accent: #8b5cf6;
          --gray-100: #f3f4f6;
          --gray-700: #374151;
          --gray-800: #1f2937;
          --success: #10b981;
          --warning: #f59e0b;
        }
        
        body {
          font-family: system-ui, -apple-system, sans-serif;
          line-height: 1.6;
          max-width: 1200px;
          margin: 0 auto;
          padding: 40px 20px;
          color: var(--gray-800);
          background: #ffffff;
        }
        
        .header {
          text-align: center;
          margin-bottom: 60px;
          padding-bottom: 30px;
          border-bottom: 3px solid var(--primary);
        }
        
        .header h1 {
          color: var(--primary);
          font-size: 2.5em;
          margin-bottom: 10px;
        }
        
        .section {
          margin-bottom: 40px;
          padding: 30px;
          background: var(--gray-100);
          border-radius: 12px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .section-title {
          color: var(--primary);
          font-size: 1.5em;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid var(--primary);
        }
        
        .compound-steps {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin: 30px 0;
        }
        
        .step-card {
          background: white;
          padding: 25px;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
          border-left: 5px solid var(--secondary);
          position: relative;
        }
        
        .step-number {
          position: absolute;
          top: -10px;
          left: 20px;
          background: var(--secondary);
          color: white;
          width: 30px;
          height: 30px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: bold;
          font-size: 14px;
        }
        
        .step-card h3 {
          color: var(--secondary);
          margin-bottom: 15px;
          margin-top: 10px;
        }
        
        .highlight-box {
          background: linear-gradient(135deg, #16a34a, #22c55e);
          color: white;
          padding: 20px;
          border-radius: 12px;
          text-align: center;
          margin: 20px 0;
        }
        
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          margin: 20px 0;
        }
        
        .stat-card {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          text-align: center;
        }
        
        .stat-value {
          font-size: 2em;
          font-weight: bold;
          color: var(--primary);
          margin: 10px 0;
        }
        
        .goal-timeline {
          background: white;
          padding: 30px;
          border-radius: 12px;
          margin: 30px 0;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }
        
        .timeline-bar {
          height: 40px;
          background: linear-gradient(to right, #e5e7eb 0%, #e5e7eb 100%);
          border-radius: 20px;
          position: relative;
          margin: 20px 0;
          overflow: hidden;
        }
        
        .timeline-progress {
          height: 100%;
          background: linear-gradient(to right, #16a34a, #22c55e);
          border-radius: 20px;
          transition: width 0.5s ease;
        }
        
        .timeline-marker {
          position: absolute;
          top: -5px;
          transform: translateX(-50%);
          background: ${isGoalAchieved ? 'var(--success)' : 'var(--warning)'};
          color: white;
          padding: 8px 12px;
          border-radius: 20px;
          font-weight: bold;
          font-size: 14px;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
        
        .timeline-marker::after {
          content: '';
          position: absolute;
          top: 100%;
          left: 50%;
          transform: translateX(-50%);
          border: 8px solid transparent;
          border-top-color: ${isGoalAchieved ? 'var(--success)' : 'var(--warning)'};
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 20px 0;
          background: white;
          border-radius: 8px;
          overflow: hidden;
        }
        
        th, td {
          padding: 12px 16px;
          text-align: left;
          border-bottom: 1px solid var(--gray-100);
        }
        
        th {
          background: var(--primary);
          color: white;
          font-weight: 500;
        }
        
        tr:nth-child(even) {
          background: var(--gray-100);
        }
        
        tr.goal-achieved {
          background: #dcfce7 !important;
          border-left: 4px solid var(--success);
        }
        
        .footer {
          text-align: center;
          margin-top: 60px;
          padding-top: 20px;
          border-top: 2px solid var(--gray-100);
          color: var(--gray-700);
        }
        
        .excel-table {
          background: white;
          border-radius: 8px;
          overflow: hidden;
          margin: 20px 0;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        .excel-table th {
          background: var(--secondary);
          color: white;
          font-size: 12px;
          padding: 8px 12px;
        }
        
        .excel-table td {
          padding: 8px 12px;
          font-size: 12px;
          border-bottom: 1px solid var(--gray-100);
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Reporte de Interés Compuesto Multiplicador™</h1>
        <h2>${compoundMultiplierContent.subtitle}</h2>
        <h2>${dream.name}</h2>
        <p>Generado el ${formattedDate}</p>
        <div style="margin-top: 15px; padding: 12px; background: rgba(255, 255, 255, 0.2); border-radius: 8px;">
          <p style="margin: 0; font-weight: 500;">${compoundMultiplierContent.tagline}</p>
        </div>
      </div>
      
      <div class="section">
        <h2 class="section-title">🎯 Cumplimiento de Meta con Multiplicador™</h2>
        
        <div style="background: linear-gradient(135deg, #16a34a, #22c55e); color: white; padding: 20px; border-radius: 12px; margin-bottom: 20px; text-align: center;">
          <h3 style="margin-bottom: 10px;">🚀 Tu Transformación con Multiplicador™</h3>
          <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 15px; margin-top: 15px;">
            <div>
              <div style="font-size: 11px; opacity: 0.8;">Certificados Iniciales</div>
              <div style="font-size: 18px; font-weight: bold;">${config.certificatesNeeded}</div>
            </div>
            <div>
              <div style="font-size: 11px; opacity: 0.8;">Certificados Finales</div>
              <div style="font-size: 18px; font-weight: bold;">${finalProjection?.totalCertificates || config.certificatesNeeded}</div>
            </div>
            <div>
              <div style="font-size: 11px; opacity: 0.8;">Multiplicador de Activos</div>
              <div style="font-size: 18px; font-weight: bold;">${((finalProjection?.totalCertificates || config.certificatesNeeded) / config.certificatesNeeded).toFixed(1)}x</div>
            </div>
          </div>
        </div>
        
        <div class="goal-timeline">
          <div style="text-align: center; margin-bottom: 20px;">
            <h3 style="color: var(--secondary); margin-bottom: 10px;">
              Meta Mensual: ${formatCurrency(config.monthlyGoal, investment.currencyFormat)}
            </h3>
            <p style="color: var(--gray-700);">
              ${isGoalAchieved 
                ? `🎉 <strong>Meta Alcanzada en el Año ${goalAchievementYear}</strong> - Progreso Final: ${finalProgress.toFixed(1)}%`
                : `⏳ Meta no alcanzada en ${config.timeframe} años - Progreso Final: ${finalProgress.toFixed(1)}%`
              }
            </p>
          </div>
          
          <div class="timeline-bar">
            <div class="timeline-progress" style="width: ${Math.min(100, (goalAchievementYear || config.timeframe) / config.timeframe * 100)}%;"></div>
            ${isGoalAchieved ? `
              <div class="timeline-marker" style="left: ${(goalAchievementYear / config.timeframe) * 100}%;">
                Año ${goalAchievementYear}
              </div>
            ` : `
              <div class="timeline-marker" style="left: 100%;">
                ${finalProgress.toFixed(1)}%
              </div>
            `}
          </div>
          
          <div style="display: flex; justify-content: space-between; font-size: 12px; color: var(--gray-700);">
            <span>Año 1</span>
            <span>Año ${Math.floor(config.timeframe / 2)}</span>
            <span>Año ${config.timeframe}</span>
          </div>
          
          <div style="margin-top: 20px; padding: 15px; background: ${isGoalAchieved ? '#dcfce7' : '#fef3c7'}; border-radius: 8px;">
            <p style="margin: 0; color: ${isGoalAchieved ? '#166534' : '#92400e'};">
              <strong>${isGoalAchieved ? '✅ Resultado:' : '⚠️ Resultado:'}</strong>
              ${isGoalAchieved 
                ? `Tu sueño "${dream.name}" se cumple en el año ${goalAchievementYear} con un ingreso mensual de ${formatCurrency(projections[goalAchievementYear - 1]?.monthlyIncome || 0, investment.currencyFormat)}.`
                : `Para alcanzar tu meta de ${formatCurrency(config.monthlyGoal, investment.currencyFormat)} mensuales, considera aumentar el número de certificados o extender el plazo de inversión.`
              }
            </p>
          </div>
        </div>
      </div>
      
      <div class="section">
        <h2 class="section-title">Los 4 Pilares del Interés Compuesto Multiplicador™</h2>
        
        <div class="compound-steps">
          ${compoundMultiplierContent.pillars.items.map((pillar, index) => `
            <div class="step-card">
              <div class="step-number">${pillar.number}</div>
              <h3>${pillar.title}</h3>
              <p style="font-weight: 600; color: #16a34a;">${pillar.description}</p>
              ${pillar.examples.map(example => `<p>• ${example}</p>`).join('')}
              ${pillar.number === 1 ? `
                <p><strong>Tu caso:</strong> ${config.certificatesNeeded} certificados → ${(finalProjection?.totalCertificates || config.certificatesNeeded)} certificados</p>
                <p><strong>Multiplicación:</strong> ${((finalProjection?.totalCertificates || config.certificatesNeeded) / config.certificatesNeeded).toFixed(1)}x más activos productivos</p>
              ` : ''}
            </div>
          `).join('')}
        </div>
        
        <div class="highlight-box">
          <h3>🎯 Resultado Multiplicador™: ${dream.name}</h3>
          <p style="font-size: 1.2em; margin: 15px 0;">
            <strong>Meta Mensual:</strong> ${formatCurrency(config.monthlyGoal, investment.currencyFormat)} | 
            <strong>Ingreso Final:</strong> ${formatCurrency(finalProjection?.monthlyIncome || 0, investment.currencyFormat)}<br>
            <strong>Activos Productivos:</strong> ${finalProjection?.totalCertificates || config.certificatesNeeded} certificados (${((finalProjection?.totalCertificates || config.certificatesNeeded) * 1000).toLocaleString()} m² productivos)
          </p>
        </div>
      </div>
      
      <div class="section">
        <h2 class="section-title">📊 Evolución Detallada</h2>
        <table>
          <thead>
            <tr>
              <th>Año</th>
              <th>Certificados</th>
              <th>Valor Certificado</th>
              <th>Patrimonio</th>
              <th>Utilidad Anual</th>
              <th>Ingreso Mensual</th>
              <th>Progreso Meta</th>
            </tr>
          </thead>
          <tbody>
            ${projections.map(p => `
              <tr ${p.year === goalAchievementYear ? 'class="goal-achieved"' : ''}>
                <td>${p.year}</td>
                <td>${p.totalCertificates}</td>
                <td>${formatCurrency(p.certificateValue, investment.currencyFormat)}</td>
                <td>${formatCurrency(p.patrimony, investment.currencyFormat)}</td>
                <td>${formatCurrency(p.yearlyUtility, investment.currencyFormat)}</td>
                <td>${formatCurrency(p.monthlyIncome, investment.currencyFormat)}</td>
                <td>${config.monthlyGoal ? ((p.monthlyIncome / config.monthlyGoal) * 100).toFixed(1) : 0}%</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      
      <div class="section">
        <h2 class="section-title">🔑 Características Clave del Interés Compuesto Multiplicador™</h2>
        
        <div style="background: linear-gradient(135deg, #8b5cf6, #6366f1); color: white; padding: 20px; border-radius: 12px; margin-bottom: 20px; text-align: center;">
          <h3 style="margin-bottom: 10px;">🌟 La Diferencia del Multiplicador™</h3>
          <p style="opacity: 0.9; margin: 0;">
            Mientras el interés compuesto tradicional multiplica dinero, el Multiplicador™ multiplica ACTIVOS REALES PRODUCTIVOS.
            No solo tienes más dinero, tienes más fuentes de ingresos permanentes.
          </p>
        </div>
        
        <ul style="list-style: none; padding: 0;">
          <li style="margin: 15px 0; padding: 15px; background: white; border-radius: 8px; border-left: 4px solid var(--primary);">
            <strong>🌱 Multiplicación de Activos:</strong> Las utilidades adquieren nuevos certificados productivos, no solo más dinero
          </li>
          <li style="margin: 15px 0; padding: 15px; background: white; border-radius: 8px; border-left: 4px solid var(--secondary);">
            <strong>⏰ Efecto Avalancha:</strong> Cada nuevo certificado acelera la adquisición de los siguientes
          </li>
          <li style="margin: 15px 0; padding: 15px; background: white; border-radius: 8px; border-left: 4px solid var(--accent);">
            <strong>🔄 Diversificación Automática:</strong> Cada certificado diversifica tu riesgo en diferentes lotes productivos
          </li>
          <li style="margin: 15px 0; padding: 15px; background: white; border-radius: 8px; border-left: 4px solid var(--success);">
            <strong>📈 Activos Tangibles:</strong> Posees árboles reales que puedes ver, tocar y heredar
          </li>
        </ul>
      </div>
      
      <div class="footer">
        <p>© ${new Date().getFullYear()} Cosecha Capital</p>
        <p style="font-weight: 600; color: #16a34a;">Interés Compuesto Multiplicador™ - La Nueva Era de Crecimiento Patrimonial</p>
        <p>Reporte generado automáticamente - ${formattedDate}</p>
      </div>
    </body>
    </html>`;

  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_Interes_Compuesto_${dream.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
  link.click();
  URL.revokeObjectURL(url);
  
  if (onStatusChange) {
    onStatusChange('complete');
  }
};

// Simple download function without hooks
const downloadDreamReport = (dream: Dream, config: DreamConfig, projections: DreamProjection[], investment: Investment) => {
  const currentDate = new Date().toLocaleDateString('es-MX', {
    year: 'numeric',
    month: 'long', 
    day: 'numeric'
  });
  
  const finalProjection = projections[projections.length - 1];
  
  const htmlContent = `<!DOCTYPE html>
<html lang="es">
<head>
  <meta charset="UTF-8">
  <title>Reporte de Sueño - ${dream.name}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 40px; }
    .header { text-align: center; margin-bottom: 40px; }
    .section { margin-bottom: 30px; padding: 20px; background: #f9f9f9; border-radius: 8px; }
    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
    th, td { padding: 10px; border: 1px solid #ddd; text-align: left; }
    th { background: #16a34a; color: white; }
  </style>
</head>
<body>
  <div class="header">
    <h1>Reporte de Sueño: ${dream.name}</h1>
    <p>Generado el ${currentDate}</p>
  </div>
  
  <div class="section">
    <h2>Resumen</h2>
    <p><strong>Meta Mensual:</strong> ${formatCurrency(config.monthlyGoal, investment.currencyFormat)}</p>
    <p><strong>Plazo:</strong> ${config.timeframe} años</p>
    <p><strong>Certificados:</strong> ${config.certificatesNeeded}</p>
    <p><strong>Ingreso Final:</strong> ${formatCurrency(finalProjection?.monthlyIncome || 0, investment.currencyFormat)}</p>
  </div>
  
  <div class="section">
    <h2>Evolución Anual</h2>
    <table>
      <tr><th>Año</th><th>Certificados</th><th>Patrimonio</th><th>Ingreso Mensual</th></tr>
      ${projections.map(p => `<tr><td>${p.year}</td><td>${p.totalCertificates}</td><td>${formatCurrency(p.patrimony, investment.currencyFormat)}</td><td>${formatCurrency(p.monthlyIncome, investment.currencyFormat)}</td></tr>`).join('')}
    </table>
  </div>
</body>
</html>`;

  const blob = new Blob([htmlContent], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Reporte_${dream.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.html`;
  link.click();
  URL.revokeObjectURL(url);
};

interface AnimatedChartsProps {
  dream: Dream;
  config: DreamConfig;
  projections: DreamProjection[];
  onContinue: () => void;
  onBack: () => void;
  investment: Investment;
  selectedComparisons: string[];
  onSelectedComparisonsChange: (comparisons: string[]) => void;
}

const AnimatedCharts: React.FC<AnimatedChartsProps> = ({ 
  dream, 
  config, 
  projections,
  onContinue,
  onBack,
  investment,
  selectedComparisons,
  onSelectedComparisonsChange
}) => {
  const [currentYear, setCurrentYear] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationSpeed, setAnimationSpeed] = useState(1);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);
  const [chartType, setChartType] = useState<'line' | 'bar' | 'area'>('line');
  const [activeDataType, setActiveDataType] = useState<'patrimony' | 'income'>('patrimony');
  const [showExcelTable, setShowExcelTable] = useState(false);
  const [downloadStatus, setDownloadStatus] = useState<'idle' | 'downloading' | 'complete'>('idle');

  const Icon = dream.icon;

  // Prepare animated data - only show data up to current year
  const getAnimatedData = () => {
    const data = [];
    
    for (let year = 1; year <= currentYear; year++) {
      const yearData: any = { year };
      
      // Cosecha Capital data
      if (selectedComparisons.includes('cosechaCapital')) {
        const projection = projections.find(p => p.year === year);
        if (projection) {
          if (activeDataType === 'patrimony') {
            yearData['Cosecha Capital'] = projection.patrimony;
          } else {
            yearData['Cosecha Capital'] = projection.monthlyIncome * 12; // Annual income
          }
        }
      }
      
      // CETES data
      if (selectedComparisons.includes('cetes')) {
        const initialInvestment = config.certificatesNeeded * investment.certificateBasePrice;
        if (activeDataType === 'patrimony') {
          yearData['CETES'] = initialInvestment * Math.pow(1 + investment.cetesRate / 100, year);
        } else {
          const patrimony = initialInvestment * Math.pow(1 + investment.cetesRate / 100, year);
          yearData['CETES'] = patrimony * (investment.cetesRate / 100);
        }
      }
      
      // Savings data
      if (selectedComparisons.includes('savings')) {
        const initialInvestment = config.certificatesNeeded * investment.certificateBasePrice;
        if (activeDataType === 'patrimony') {
          yearData['Ahorro Tradicional'] = initialInvestment * Math.pow(1 + investment.savingsRate / 100, year);
        } else {
          const patrimony = initialInvestment * Math.pow(1 + investment.savingsRate / 100, year);
          yearData['Ahorro Tradicional'] = patrimony * (investment.savingsRate / 100);
        }
      }
      
      // Real Estate data
      if (selectedComparisons.includes('realEstate')) {
        const initialInvestment = config.certificatesNeeded * investment.certificateBasePrice;
        if (activeDataType === 'patrimony') {
          yearData['Bienes Raíces'] = initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, year);
        } else {
          const patrimony = initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, year);
          yearData['Bienes Raíces'] = patrimony * (investment.realEstateRent / 100);
        }
      }
      
      data.push(yearData);
    }
    
    return data;
  };

  const animatedData = getAnimatedData();

  // Animation control functions
  const startAnimation = () => {
    setIsAnimating(true);
    
    const interval = setInterval(() => {
      setCurrentYear(prev => {
        const nextYear = prev + 1;
        if (nextYear > config.timeframe) {
          setIsAnimating(false);
          return config.timeframe;
        }
        return nextYear;
      });
    }, 2000 / animationSpeed);
    
    setIntervalId(interval);
  };

  const pauseAnimation = () => {
    setIsAnimating(false);
    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  };

  const resetAnimation = () => {
    pauseAnimation();
    setCurrentYear(1);
  };

  const skipToEnd = () => {
    pauseAnimation();
    setCurrentYear(config.timeframe);
  };

  const changeSpeed = (speed: number) => {
    setAnimationSpeed(speed);
    if (isAnimating) {
      pauseAnimation();
      setAnimationSpeed(speed);
      startAnimation();
    }
  };

  // Cleanup interval on unmount
  useEffect(() => {
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [intervalId]);

  // Get current values for display
  const getCurrentValues = () => {
    const currentProjection = projections.find(p => p.year === currentYear);
    const initialInvestment = config.certificatesNeeded * investment.certificateBasePrice;
    
    // CETES values - depend on reinvestment setting
    let cetesPatrimony, cetesIncome;
    if (investment.reinvestProfits) {
      cetesPatrimony = initialInvestment * Math.pow(1 + investment.cetesRate / 100, currentYear);
      cetesIncome = cetesPatrimony * (investment.cetesRate / 100);
    } else {
      cetesPatrimony = initialInvestment;
      cetesIncome = currentYear > 4 ? initialInvestment * (investment.cetesRate / 100) : 0;
    }
    
    // Savings values - depend on reinvestment setting
    let savingsPatrimony, savingsIncome;
    if (investment.reinvestProfits) {
      savingsPatrimony = initialInvestment * Math.pow(1 + investment.savingsRate / 100, currentYear);
      savingsIncome = savingsPatrimony * (investment.savingsRate / 100);
    } else {
      savingsPatrimony = initialInvestment;
      savingsIncome = currentYear > 4 ? initialInvestment * (investment.savingsRate / 100) : 0;
    }
    
    // Real Estate values - depend on reinvestment setting
    let realEstatePatrimony, realEstateIncome;
    if (investment.reinvestProfits) {
      realEstatePatrimony = initialInvestment * Math.pow(1 + investment.realEstateAppreciation / 100, currentYear);
      realEstateIncome = realEstatePatrimony * (investment.realEstateRent / 100);
    } else {
      realEstatePatrimony = initialInvestment;
      realEstateIncome = currentYear > 4 ? initialInvestment * (investment.realEstateRent / 100) : 0;
    }
    
    return {
      cosechaCapital: {
        patrimony: currentProjection?.patrimony || 0,
        income: currentProjection ? currentProjection.monthlyIncome * 12 : 0
      },
      cetes: {
        patrimony: cetesPatrimony,
        income: cetesIncome
      },
      savings: {
        patrimony: savingsPatrimony,
        income: savingsIncome
      },
      realEstate: {
        patrimony: realEstatePatrimony,
        income: realEstateIncome
      }
    };
  };

  const currentValues = getCurrentValues();

  const generateDreamReport = () => {
    setDownloadStatus('downloading');
    
    setTimeout(() => {
      generateCompoundInterestReport(dream, config, projections, investment, setDownloadStatus);
      setDownloadStatus('complete');
      
      setTimeout(() => setDownloadStatus('idle'), 2000);
    }, 1000);
  };

  if (showExcelTable) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-6xl mx-auto"
      >
        <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-8 text-white">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <BarChart3 className="w-8 h-8" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Tabla de Evolución Detallada - {dream.name}</h2>
                <p className="text-blue-100">Análisis detallado año por año de tu sueño con {config.certificatesNeeded} certificados</p>
              </div>
            </div>
          </div>
          
          <div className="p-8">
            <ExcelTableV5 
              initialCertificates={config.certificatesNeeded} 
              years={config.timeframe}
              investment={investment}
            />
            
            <div className="mt-8 flex justify-center gap-4">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowExcelTable(false)}
                className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Volver a Gráficos Animados</span>
              </motion.button>
              
              <button
                onClick={generateDreamReport}
                disabled={downloadStatus === 'downloading'}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg shadow-md transition-all duration-300 transform hover:scale-105 ${
                  downloadStatus === 'complete' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                {downloadStatus === 'idle' && (
                  <>
                    <Download className="w-5 h-5" />
                    <span className="font-medium">Descargar Reporte de Sueño</span>
                  </>
                )}
                {downloadStatus === 'downloading' && (
                  <>
                    <FileText className="w-5 h-5 animate-pulse" />
                    <span className="font-medium">Generando Reporte...</span>
                  </>
                )}
                {downloadStatus === 'complete' && (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">¡Reporte Descargado!</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Render chart based on type
  const renderChart = () => {
    const chartElements = [
      { dataKey: 'Cosecha Capital', color: '#16a34a', strokeWidth: 3 },
      { dataKey: 'CETES', color: '#0284c7', strokeWidth: 2, strokeDasharray: '5 5' },
      { dataKey: 'Ahorro Tradicional', color: '#7c3aed', strokeWidth: 2, strokeDasharray: '5 5' },
      { dataKey: 'Bienes Raíces', color: '#ea580c', strokeWidth: 2, strokeDasharray: '5 5' }
    ].filter(element => selectedComparisons.includes(
      element.dataKey === 'Cosecha Capital' ? 'cosechaCapital' :
      element.dataKey === 'CETES' ? 'cetes' :
      element.dataKey === 'Ahorro Tradicional' ? 'savings' :
      'realEstate'
    ));

    const commonProps = {
      data: animatedData,
      margin: { top: 20, right: 30, left: 20, bottom: 10 }
    };

    switch (chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <RechartsLineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={(value) => formatCurrency(value, investment.currencyFormat)} />
              <Tooltip formatter={(value) => formatCurrency(value as number, investment.currencyFormat)} />
              <Legend />
              {chartElements.map((element, index) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={element.dataKey}
                  stroke={element.color}
                  strokeWidth={element.strokeWidth}
                  strokeDasharray={element.strokeDasharray}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </RechartsLineChart>
          </ResponsiveContainer>
        );
      
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={(value) => formatCurrency(value, investment.currencyFormat)} />
              <Tooltip formatter={(value) => formatCurrency(value as number, investment.currencyFormat)} />
              <Legend />
              {chartElements.map((element, index) => (
                <Bar
                  key={index}
                  dataKey={element.dataKey}
                  fill={element.color}
                  radius={[4, 4, 0, 0]}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );
      
      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="year" />
              <YAxis tickFormatter={(value) => formatCurrency(value, investment.currencyFormat)} />
              <Tooltip formatter={(value) => formatCurrency(value as number, investment.currencyFormat)} />
              <Legend />
              {chartElements.map((element, index) => (
                <Area
                  key={index}
                  type="monotone"
                  dataKey={element.dataKey}
                  stroke={element.color}
                  fill={element.color}
                  fillOpacity={0.3}
                  strokeWidth={element.strokeWidth}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );
      
      default:
        return null;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-6xl mx-auto"
    >
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden">
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-8 text-white">
          <div className="flex items-center gap-6">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <BarChart3 className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Gráficos Animados - {dream.name}</h2>
              <p className="text-indigo-100">Visualiza la evolución temporal de todas las inversiones</p>
            </div>
          </div>
        </div>
        
        <div className="p-8 space-y-8">
          {/* Chart Configuration */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">Configuración del Gráfico</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Chart Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Tipo de Gráfico</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    onClick={() => setChartType('line')}
                    className={`p-3 rounded-lg transition-colors ${
                      chartType === 'line'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <LineChart className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-xs">Líneas</div>
                  </button>
                  <button
                    onClick={() => setChartType('bar')}
                    className={`p-3 rounded-lg transition-colors ${
                      chartType === 'bar'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <BarChart3 className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-xs">Barras</div>
                  </button>
                  <button
                    onClick={() => setChartType('area')}
                    className={`p-3 rounded-lg transition-colors ${
                      chartType === 'area'
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Activity className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-xs">Área</div>
                  </button>
                </div>
              </div>
              
              {/* Data Type Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">Datos a Mostrar</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setActiveDataType('patrimony')}
                    className={`p-3 rounded-lg transition-colors ${
                      activeDataType === 'patrimony'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <TrendingUp className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-xs">Patrimonio</div>
                  </button>
                  <button
                    onClick={() => setActiveDataType('income')}
                    className={`p-3 rounded-lg transition-colors ${
                      activeDataType === 'income'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <DollarSign className="w-5 h-5 mx-auto mb-1" />
                    <div className="text-xs">Ingresos</div>
                  </button>
                </div>
              </div>
            </div>

            {/* Investment Selector */}
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-3">Inversiones a Comparar</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { id: 'cosechaCapital', name: 'Cosecha Capital', color: '#16a34a', icon: '🌱' },
                  { id: 'cetes', name: 'CETES', color: '#0284c7', icon: '📊' },
                  { id: 'savings', name: 'Ahorro', color: '#7c3aed', icon: '🏦' },
                  { id: 'realEstate', name: 'Bienes Raíces', color: '#ea580c', icon: '🏠' }
                ].map(option => (
                  <button
                    key={option.id}
                    onClick={() => {
                      onSelectedComparisonsChange(
                        selectedComparisons.includes(option.id) 
                          ? selectedComparisons.filter(id => id !== option.id)
                          : [...selectedComparisons, option.id]
                      );
                    }}
                    className={`p-3 rounded-lg border-2 transition-all ${
                      selectedComparisons.includes(option.id)
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-center">
                      <div className="text-2xl mb-1">{option.icon}</div>
                      <div className="text-sm font-medium">{option.name}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Animated Chart */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-800">
                {activeDataType === 'patrimony' ? 'Evolución del Patrimonio' : 'Evolución de Ingresos Anuales'}
              </h3>
              <div className="text-sm text-gray-600">
                Mostrando hasta año {currentYear}
              </div>
            </div>
            
            <div className="h-96">
              {renderChart()}
            </div>

            {/* Animation Controls */}
            <div className="flex justify-between items-center mt-6">
              <div className="flex justify-center items-center gap-4">
                {/* Speed Control */}
                <div className="flex gap-2">
                  {[0.5, 1, 2, 4].map(speed => (
                    <button
                      key={speed}
                      onClick={() => changeSpeed(speed)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                        animationSpeed === speed
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {speed}x
                    </button>
                  ))}
                </div>
                
                <button
                  onClick={isAnimating ? pauseAnimation : startAnimation}
                  className={`p-3 rounded-full transition-colors ${
                    isAnimating 
                      ? 'bg-red-600 hover:bg-red-700 text-white' 
                      : 'bg-green-600 hover:bg-green-700 text-white'
                  }`}
                >
                  {isAnimating ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
                </button>
                
                <button
                  onClick={skipToEnd}
                  className="p-3 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors"
                >
                  <SkipForward className="w-6 h-6" />
                </button>
                
                <button
                  onClick={resetAnimation}
                  className="p-3 bg-gray-600 text-white rounded-full hover:bg-gray-700 transition-colors"
                >
                  <RotateCcw className="w-6 h-6" />
                </button>
              </div>
              
              {/* Dynamic Year Indicator */}
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-sm text-gray-500">Progreso</div>
                  <div className="text-xs text-gray-400">{config.timeframe} años total</div>
                </div>
                <div className="flex items-center justify-center w-20 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-xl shadow-lg">
                  <div className="text-center">
                    <div className="text-2xl font-bold leading-none">
                      {currentYear}
                    </div>
                    <div className="text-xs opacity-80">
                      año
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Current Values Display */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-6">
              Valores Actuales - Año {currentYear}
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {selectedComparisons.includes('cosechaCapital') && (
                <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🌱</span>
                    <h4 className="font-medium text-green-800">Cosecha Capital</h4>
                  </div>
                  <div className="text-2xl font-bold text-green-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={
                      activeDataType === 'patrimony' 
                        ? currentValues.cosechaCapital.patrimony 
                        : currentValues.cosechaCapital.income
                      }
                      currencyFormat={investment.currencyFormat}
                      duration={1.4}
                      staggerDelay={0.1}
                    />
                  </div>
                  <div className="text-sm text-green-600">
                    {activeDataType === 'patrimony' ? 'Patrimonio' : 'Ingreso anual'}
                  </div>
                </div>
              )}
              
              {selectedComparisons.includes('cetes') && (
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">📊</span>
                    <h4 className="font-medium text-blue-800">CETES</h4>
                  </div>
                  <div className="text-2xl font-bold text-blue-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={
                      activeDataType === 'patrimony' 
                        ? currentValues.cetes.patrimony 
                        : currentValues.cetes.income
                      }
                      currencyFormat={investment.currencyFormat}
                      duration={1.4}
                      staggerDelay={0.1}
                    />
                  </div>
                  <div className="text-sm text-blue-600">
                    {activeDataType === 'patrimony' ? 'Patrimonio' : 'Ingreso anual'}
                  </div>
                </div>
              )}
              
              {selectedComparisons.includes('savings') && (
                <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🏦</span>
                    <h4 className="font-medium text-purple-800">Ahorro</h4>
                  </div>
                  <div className="text-2xl font-bold text-purple-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={
                      activeDataType === 'patrimony' 
                        ? currentValues.savings.patrimony 
                        : currentValues.savings.income
                      }
                      currencyFormat={investment.currencyFormat}
                      duration={1.4}
                      staggerDelay={0.1}
                    />
                  </div>
                  <div className="text-sm text-purple-600">
                    {activeDataType === 'patrimony' ? 'Patrimonio' : 'Ingreso anual'}
                  </div>
                </div>
              )}
              
              {selectedComparisons.includes('realEstate') && (
                <div className="bg-gradient-to-br from-orange-50 to-orange-100 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🏠</span>
                    <h4 className="font-medium text-orange-800">Bienes Raíces</h4>
                  </div>
                  <div className="text-2xl font-bold text-orange-700 relative overflow-hidden">
                    <AnimatedNumberDisplay
                      value={
                      activeDataType === 'patrimony' 
                        ? currentValues.realEstate.patrimony 
                        : currentValues.realEstate.income
                      }
                      currencyFormat={investment.currencyFormat}
                      duration={1.4}
                      staggerDelay={0.1}
                    />
                  </div>
                  <div className="text-sm text-orange-600">
                    {activeDataType === 'patrimony' ? 'Patrimonio' : 'Ingreso anual'}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onBack}
              className="px-6 py-3 bg-gray-100 text-gray-700 font-medium rounded-xl hover:bg-gray-200 transition-all duration-300 flex items-center gap-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span>Volver</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onContinue}
              className="px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <span>Continuar</span>
              <ChevronRight className="w-5 h-5" />
            </motion.button>
          </div>
          
          {/* Additional Action Buttons */}
          <div className="flex justify-center gap-4 mt-8">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowExcelTable(true)}
              className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <BarChart3 className="w-5 h-5" />
              <span>Ver Tabla Excel Detallada</span>
            </motion.button>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                // Generate compound interest report
                generateCompoundInterestReport(dream, config, projections, investment);
              }}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-semibold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2"
            >
              <DollarSign className="w-5 h-5" />
              <span>Descargar Reporte de Interés Compuesto</span>
            </motion.button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default AnimatedCharts;